/* =============================================================================
   BRON — Product detail page interactions
   - Shade selector (binds to Shopify variants via data-variant-id)
   - Quantity +/- stepper
   - View-strip (swaps hero image; shows "photography pending" for empty slots)
   - Purchase options: supply ladder rows + One-Time / Subscribe mode toggle
   - Async Add to Kit (POSTs to /cart/add.js; dispatches `bron:cart:added`)

   Each hero instance is initialized independently so multiple BRON heroes on
   one page do not collide.

   Purchase-row contract (all attributes optional, legacy rows still work):
     data-qty          units of the main variant
     data-price-one    total in cents, one-time mode
     data-price-sub    total in cents, subscribe mode
     data-plan-sub     Loop selling plan id used in subscribe mode
     data-gift-one     "variantId:qty,variantId:qty" extra lines added in
                       one-time mode (the automatic BXGY zeroes them at checkout)
     data-gift-sub     same, for subscribe mode (usually empty — Shopify's
                       automatic BXGY never applies to subscription lines)
     data-price        legacy single price (used when the mode prices are absent)
     data-selling-plan legacy plan id (used in one-time mode when present)
   The hero root carries data-bron-purchase-mode="one|sub"; CSS shows/hides the
   .mo / .ms spans inside each row from that attribute.
   ============================================================================= */
(function () {
  'use strict';

  /* ---------- helpers ---------- */
  function moneyUSD(cents) {
    // Shopify gives prices in cents. Render as "$36 USD".
    var dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:document.querySelector('[data-bron-hydration-hero]').dataset.currency || 'USD'}).format(cents/100);
  }

  function clampInt(v, min, max) {
    v = parseInt(v, 10);
    if (isNaN(v)) return min;
    return Math.max(min, Math.min(max, v));
  }

  function parseLines(spec) {
    // "123:1,456:2" -> [{id:123, quantity:1}, {id:456, quantity:2}]
    if (!spec) return [];
    return spec.split(',').map(function (pair) {
      var b = pair.split(':');
      var id = parseInt(b[0], 10);
      if (!id) return null;
      return { id: id, quantity: clampInt(b[1] || '1', 1, 99) };
    }).filter(Boolean);
  }

  /* ---------- per-hero initializer ---------- */
  function initHero(root) {
    if (!root || root.dataset.bronInit === '1') return;
    root.dataset.bronInit = '1';

    var heroImg          = root.querySelector('[data-bron-hero-img]');
    var heroPlaceholder  = root.querySelector('[data-bron-hero-placeholder]');
    var placeholderName  = root.querySelector('[data-bron-placeholder-name]');
    var swatches         = root.querySelectorAll('[data-bron-swatch]');
    var shadeCurrent     = root.querySelector('[data-bron-shade-current]');
    var qtyVal           = root.querySelector('[data-bron-qty-val]');
    var qtyMinus         = root.querySelector('[data-bron-qty-minus]');
    var qtyPlus          = root.querySelector('[data-bron-qty-plus]');
    var addBtn           = root.querySelector('[data-bron-add]');
    var priceEl          = root.querySelector('[data-bron-price]');
    var hiddenVariant    = root.querySelector('[data-bron-variant-id]');
    var viewButtons      = root.querySelectorAll('[data-bron-view]');
    var sellingPlanInput = root.querySelector('[data-bron-selling-plan]');
    var giftInput        = root.querySelector('[data-bron-gift-lines]');
    var purchaseBtns     = root.querySelectorAll('[data-bron-purchase]');
    var modeBtns         = root.querySelectorAll('[data-bron-mode]');
    /* The sticky buy bar gets re-parented to <body> by the section's inline
       script, so resolve it by id (the node reference survives the move). */
    var stickyBar        = document.getElementById('bron-sticky-' + (root.getAttribute('data-bron-hero') || ''));
    var stickyPx         = stickyBar ? stickyBar.querySelector('.px') : null;

    /* Preload the real product photos so swap is instant */
    root.querySelectorAll('[data-bron-view][data-src]').forEach(function (btn) {
      var src = btn.getAttribute('data-src');
      if (src) { var im = new Image(); im.src = src; }
    });

    /* ----- Shade selector → variant binding ----- */
    function selectSwatch(btn) {
      swatches.forEach(function (s) { s.setAttribute('aria-pressed', 'false'); });
      btn.setAttribute('aria-pressed', 'true');

      if (shadeCurrent) {
        var num = btn.getAttribute('data-num') || '';
        var name = btn.getAttribute('data-name') || '';
        shadeCurrent.textContent = (num ? num + ' · ' : '') + name.toUpperCase();
      }

      var variantId   = btn.getAttribute('data-variant-id');
      var variantPx   = btn.getAttribute('data-variant-price'); // in cents
      var variantAvail = btn.getAttribute('data-variant-available') === 'true';

      if (variantId && hiddenVariant) {
        hiddenVariant.value = variantId;
      }
      if (variantPx && priceEl) {
        priceEl.textContent = moneyUSD(parseInt(variantPx, 10));
      }
      if (variantPx && stickyPx) {
        stickyPx.textContent = moneyUSD(parseInt(variantPx, 10));
      }
      if (addBtn) {
        if (variantId && !variantAvail) {
          addBtn.disabled = true;
        root.querySelector('[data-hydration-status]').textContent='';
          var labelEl = addBtn.querySelector('[data-bron-add-label]');
          if (labelEl) labelEl.textContent = 'Sold Out';
        } else {
          addBtn.disabled = false;
          var labelEl2 = addBtn.querySelector('[data-bron-add-label]');
          if (labelEl2 && labelEl2.dataset.defaultLabel) {
            labelEl2.textContent = labelEl2.dataset.defaultLabel;
          }
        }
      }
    }

    swatches.forEach(function (s) {
      s.addEventListener('click', function () { selectSwatch(s); });
    });

    /* ----- Purchase options: supply rows × One-Time / Subscribe mode ----- */
    function currentMode() {
      return root.getAttribute('data-bron-purchase-mode') === 'sub' ? 'sub' : 'one';
    }

    function applyPurchase(btn) {
      var sub = currentMode() === 'sub';

      var plan = sub
        ? (btn.getAttribute('data-plan-sub') || '')
        : (btn.getAttribute('data-selling-plan') || '');
      if (sellingPlanInput) {sellingPlanInput.value = plan;sellingPlanInput.disabled = !plan;}

      var q = btn.getAttribute('data-qty');
      if (q && qtyVal) qtyVal.textContent = q;
      if(q && root.querySelector('[data-hydration-qty]')) root.querySelector('[data-hydration-qty]').value=q;

      var px = sub ? btn.getAttribute('data-price-sub') : btn.getAttribute('data-price-one');
      if (!px) px = btn.getAttribute('data-price'); // legacy rows
      if (px && priceEl) priceEl.textContent = moneyUSD(parseInt(px, 10));
      if (px && stickyPx) stickyPx.textContent = moneyUSD(parseInt(px, 10));

      var gifts = sub ? (btn.getAttribute('data-gift-sub') || '') : (btn.getAttribute('data-gift-one') || '');
      if (giftInput) giftInput.value = gifts;

      /* Each tier can carry its own hero photo (bottle / bottle + bar / bottle + cream). */
      var src = sub ? root.getAttribute('data-single-image') : (btn.getAttribute('data-hero-src') || root.getAttribute('data-single-image'));
      if (src && heroImg) {
        if (heroImg.getAttribute('src') !== src) {
          heroImg.removeAttribute('srcset'); heroImg.setAttribute('src', src);
          if (heroPlaceholder) heroPlaceholder.classList.remove('is-on');
          viewButtons.forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
        }
        var frame = root.querySelector('[data-bron-hero-frame]');
        if (frame) frame.classList.toggle('is-pair', !sub && btn.getAttribute('data-hero-pair') === '1');
      }
    }

    function selectedPurchase() {
      return root.querySelector('[data-bron-purchase][aria-pressed="true"]') || purchaseBtns[0] || null;
    }

    function selectPurchase(btn) {
      purchaseBtns.forEach(function (p) { p.setAttribute('aria-pressed', 'false'); });
      btn.setAttribute('aria-pressed', 'true');
      applyPurchase(btn);
    }
    purchaseBtns.forEach(function (p) {
      p.addEventListener('click', function () { selectPurchase(p); });
    });

    function selectMode(btn) {
      modeBtns.forEach(function (m) { m.setAttribute('aria-pressed', 'false'); });
      btn.setAttribute('aria-pressed', 'true');
      root.setAttribute('data-bron-purchase-mode', btn.getAttribute('data-bron-mode') === 'sub' ? 'sub' : 'one');
      var cur = selectedPurchase();
      if (cur) applyPurchase(cur);
    }
    modeBtns.forEach(function (m) {
      m.addEventListener('click', function () { selectMode(m); });
    });

    /* Sync price/plan/gift inputs to whatever the markup pre-selected. */
    if (modeBtns.length) {
      var pressedMode = root.querySelector('[data-bron-mode][aria-pressed="true"]') || modeBtns[0];
      root.setAttribute('data-bron-purchase-mode', pressedMode.getAttribute('data-bron-mode') === 'sub' ? 'sub' : 'one');
    }
    var initial = selectedPurchase();
    if (initial) applyPurchase(initial);
    if (root.dataset.available !== 'true' && addBtn) {addBtn.disabled=true;addBtn.querySelector('[data-bron-add-label]').textContent='Unavailable';}
    var addForm=root.querySelector('[data-bron-add-form]');
    if(addForm) addForm.addEventListener('submit',function(e){e.preventDefault();if(addBtn && !addBtn.disabled)addBtn.click();});

    /* ----- Quantity stepper ----- */
    if (qtyMinus) qtyMinus.addEventListener('click', function () {
      if (!qtyVal) return;
      qtyVal.textContent = clampInt(parseInt(qtyVal.textContent, 10) - 1, 1, 9);
    });
    if (qtyPlus) qtyPlus.addEventListener('click', function () {
      if (!qtyVal) return;
      qtyVal.textContent = clampInt(parseInt(qtyVal.textContent, 10) + 1, 1, 9);
    });

    /* ----- View strip — swap hero image / show placeholder overlay ----- */
    viewButtons.forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.getAttribute('aria-pressed') === 'true') return;
        viewButtons.forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');

        var src = b.getAttribute('data-src');
        var frame = root.querySelector('[data-bron-hero-frame]');
        if (frame) frame.classList.remove('is-pair'); // view tiles are always single-bottle shots
        if (src && heroImg) {
          heroImg.removeAttribute('srcset'); heroImg.setAttribute('src', src);
          if (heroPlaceholder) heroPlaceholder.classList.remove('is-on');
        } else {
          var placeholderLabel = b.getAttribute('data-placeholder') || 'Detail';
          if (placeholderName) placeholderName.textContent = placeholderLabel + ' View';
          if (heroPlaceholder) heroPlaceholder.classList.add('is-on');
        }
      });
    });

    /* ----- Add to Kit — async POST to /cart/add.js ----- */
    if (addBtn) {
      addBtn.addEventListener('click', function (ev) {
        ev.preventDefault();
        if (addBtn.disabled) return;
        var variantId = hiddenVariant ? hiddenVariant.value : '';
        var quantity  = qtyVal ? clampInt(parseInt(qtyVal.textContent, 10), 1, 99) : 1;
        var sellingPlan = sellingPlanInput ? sellingPlanInput.value : '';
        if (!variantId) return;

        var main = { id: variantId, quantity: quantity };
        if (sellingPlan) main.selling_plan = parseInt(sellingPlan, 10);
        /* Gift lines ride along as one-time lines; checkout's automatic
           discount zeroes them. They are never attached to the selling plan. */
        var items = [main].concat(parseLines(giftInput ? giftInput.value : ''));

        addBtn.disabled = true;
        root.querySelector('[data-hydration-status]').textContent='';
        document.dispatchEvent(new CustomEvent('bron:cart:adding'));
        var labelEl = addBtn.querySelector('[data-bron-add-label]');
        var defaultLabel = labelEl ? labelEl.textContent : '';
        if (labelEl) labelEl.textContent = 'Adding…';

        fetch('/cart.js', {headers:{'Accept':'application/json'}})
          .then(function(r){if(!r.ok)throw new Error('Could not check your cart. Please try again.');return r.json();})
          .then(function(cart){
            items = items.filter(function(item,index){
              if(index === 0) return true;
              return !cart.items.some(function(line){return Number(line.variant_id) === Number(item.id) && !line.selling_plan_allocation;});
            });
            return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
            });
          })
          .then(function (r) {
            return r.json().then(function(data){if(!r.ok)throw new Error(data.description || data.message || 'Could not add this plan. Please try again.');return data;});
          })
          .then(function (payload) {
            root.querySelector('[data-hydration-status]').textContent='Added to your cart.';
            // BRON cart drawer listens for this and opens itself.
            // Header cart count node also listens and refreshes.
            document.dispatchEvent(new CustomEvent('bron:cart:added', { detail: payload }));

            if (labelEl) labelEl.textContent = 'Added ▸';
            setTimeout(function () {
              if (labelEl) labelEl.textContent = defaultLabel;
              addBtn.disabled = false;
            }, 1400);
          })
          .catch(function (error) {
            root.querySelector('[data-hydration-status]').textContent=error.message;
            document.dispatchEvent(new CustomEvent('bron:cart:error'));
            if (labelEl) labelEl.textContent = 'Try Again';
            setTimeout(function () {
              if (labelEl) labelEl.textContent = defaultLabel;
              addBtn.disabled = false;
            }, 1400);
          });
      });
    }
  }

  /* ---------- bootstrap ---------- */
  function init() {
    document.querySelectorAll('[data-bron-hydration-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('shopify:section:load', init);
  } else {
    init();
  }
})();
